import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api';
import { ProspectList } from '../../types/prospectList';
import { AxiosError } from 'axios';
import { RootState } from '..';

// Define ProspectionViewType values
export const ProspectionViewValues = {
  NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR: 'NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR',
  NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR: 'NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR',
  NEW_ESTABLISHMENTS_NEW_COMPANIES: 'NEW_ESTABLISHMENTS_NEW_COMPANIES',
  NON_CLIENT_FROM_CLIENT_COMPANIES: 'NON_CLIENT_FROM_CLIENT_COMPANIES',
} as const;

export type ProspectionViewType = typeof ProspectionViewValues[keyof typeof ProspectionViewValues];

// Define interfaces
export interface Address {
  numeroVoie?: string;
  typeVoie?: string;
  libelleVoie?: string;
  postalCode?: string;
  city?: string;
}

export interface LinkedAdherent {
  id: number;
  name: string;
  siret: string;
  address?: Address;
}

export interface Establishment {
  siret: string;
  name: string;
  siren: string;
  nafCode: string;
  trancheEffectifs?: string;
  categorieEntreprise?: string;
  creationDate: string;
  numeroVoie?: string;
  typeVoie?: string;
  libelleVoie?: string;
  postalCode?: string;
  city?: string;
  companyId?: number;
  companyName?: string;
  isClientCompany?: boolean;
  linkedAdherents?: LinkedAdherent[];
}

export interface NafCode {
  code: string;
  intitule: string;
}

interface FetchParams {
  page: number;
  limit?: number;
  perPage?: number;
  dateFrom?: string;
  dateTo?: string;
  nafCode?: string;
  postalCode?: string;
  postalCodes?: string[];
  city?: string;
  departement?: string;
  nafCodes?: string[];
  excludeMicroEntreprises?: boolean;
  trancheEffectifsMin?: string;
  trancheEffectifsMax?: string;
  categorieEntreprise?: string;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: string;
  excludedProspectListIds?: number[];
  excludeProspectsMode?: 'siret' | 'siren';
}

interface FetchResponsePagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface FetchResponse {
  establishments: Establishment[];
  pagination: FetchResponsePagination;
}

// Define loading states for each view
interface ViewSpecificState {
  establishments: Establishment[] | undefined;
  isLoading: boolean;
  error: string | null;
  total: number;
  page: number;
  totalPages: number;
}

interface NouveauxAdherentsState {
  // Data for each view stored separately to avoid confusion
  viewData: {
    [ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES]: ViewSpecificState;
    [ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR]: ViewSpecificState;
    [ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR]: ViewSpecificState;
    [ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES]: ViewSpecificState;
  };
  nafCodes: NafCode[] | undefined;
  dateFrom: string;
  dateTo: string;
  departement: string;
  selectedNafCodes: string[];
  postalCodes: string[];
  excludeMicroEntreprises: boolean;
  searchTerm: string;
  trancheEffectifsMin: string;
  trancheEffectifsMax: string;
  categorieEntreprise: string;
  currentView: ProspectionViewType;
  requestSource: string;
  isLoadingNafCodes: boolean;
  nafCodesError: string | null;
  sortBy: string;
  sortOrder: string;
  prospectLists: ProspectList[];
  isLoadingProspectLists: boolean;
  prospectListsError: string | null;
  excludedProspectListIds: number[];
  excludeProspectsMode: 'siret' | 'siren';
}

// Create empty view state
const createEmptyViewState = (): ViewSpecificState => ({
  establishments: undefined, // Initialize as undefined to distinguish from empty results
  isLoading: false,
  error: null,
  total: 0,
  page: 1,
  totalPages: 0,
});



// Helper function to standardize API response handling
const standardizeApiResponse = (responseData: any): FetchResponse => {
  // Handle case where there's no data
  if (!responseData) {
    console.error('Empty response received from API');
    return {
      establishments: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10,
        pages: 0
      }
    };
  }
  
  // Handle different response formats
  let establishments = [];
  let pagination = {
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  };
  
  // If the response has a 'data' property instead of 'establishments'
  if (responseData.data && !responseData.establishments) {
    establishments = Array.isArray(responseData.data) ? responseData.data : [];
    
    // Map establishments to ensure consistent format
    establishments = establishments.map((item: any) => {
      const uniteLegale = item.uniteLegale || {};
      
      // Determine the company name
      let name = '';
      if (uniteLegale.denominationUniteLegale) {
        name = uniteLegale.denominationUniteLegale;
      } else if (uniteLegale.nomUniteLegale) {
        name = uniteLegale.nomUniteLegale;
        if (uniteLegale.prenomUsuelUniteLegale) {
          name = `${uniteLegale.prenomUsuelUniteLegale} ${name}`;
        }
      }
      
      return {
        siret: item.siret || '',
        name: name || item.name || '',
        siren: item.siren || '',
        nafCode: item.activitePrincipaleEtablissement || item.nafCode || '',
        trancheEffectifs: uniteLegale.trancheEffectifsUniteLegale || item.trancheEffectifs || '',
        categorieEntreprise: uniteLegale.categorieEntreprise || item.categorieEntreprise || '',
        creationDate: item.dateCreationEtablissement || item.creationDate || '',
        numeroVoie: item.numeroVoieEtablissement || item.numeroVoie || '',
        typeVoie: item.typeVoieEtablissement || item.typeVoie || '',
        libelleVoie: item.libelleVoieEtablissement || item.libelleVoie || '',
        postalCode: item.codePostalEtablissement || item.postalCode || '',
        city: item.libelleCommuneEtablissement || item.city || '',
        companyId: item.companyId,
        companyName: item.companyName,
        linkedAdherents: item.linkedAdherents?.map((adherent: any) => ({
          id: adherent.id,
          name: adherent.name,
          siret: adherent.siret,
          address: adherent.address
        })) || []
      };
    });
    
    // Extract pagination info
    if (responseData.pagination) {
      pagination = responseData.pagination;
    }
  } else if (responseData.establishments) {
    // Standard format with establishments property
    establishments = Array.isArray(responseData.establishments) ? responseData.establishments : [];
    
    // Extract pagination info
    if (responseData.pagination) {
      pagination = responseData.pagination;
    }
  } else if (Array.isArray(responseData)) {
    // Handle case where the response is just an array of establishments
    establishments = responseData;
  }
  
  return {
    establishments,
    pagination
  } as FetchResponse;
};

// Helper function for error handling in async thunks
const handleApiError = (error: any, endpointName: string): string => {
  const axiosError = error as AxiosError;
  const message = axiosError.message || 'Une erreur est survenue';
  console.error(`API Error in ${endpointName}:`, error);
  return `${message} (${endpointName})`;
};

// Convert filter parameters to API parameters
const createApiParams = (filters: FetchParams): Record<string, any> => {
  const apiParams: Record<string, any> = { ...filters };
  
  // Handle nafCodes array (priority) or single nafCode
  if (filters.nafCodes && filters.nafCodes.length > 0) {
    apiParams.nafCodes = filters.nafCodes;
    delete apiParams.nafCode;
  } else if (filters.nafCode && filters.nafCode !== '') {
    apiParams.nafCodes = [filters.nafCode];
    delete apiParams.nafCode;
  } else {
    delete apiParams.nafCode;
    delete apiParams.nafCodes;
  }
  
  // Handle postalCodes array (priority) or single postalCode
  if (filters.postalCodes && filters.postalCodes.length > 0) {
    apiParams.postalCodes = filters.postalCodes;
    delete apiParams.postalCode;
  } else if (filters.postalCode && filters.postalCode !== '') {
    apiParams.postalCodes = [filters.postalCode];
    delete apiParams.postalCode;
  } else {
    delete apiParams.postalCode;
    delete apiParams.postalCodes;
  }
  
  return apiParams;
};

// Create async thunks
interface FetchPayload {
  establishments: Establishment[];
  pagination: FetchResponsePagination;
}

export const fetchNewCompanies = createAsyncThunk(
  'nouveauxAdherents/fetchNewCompanies',
  async (params: FetchParams, { rejectWithValue }) => {
    try {
      const { excludedProspectListIds, excludeProspectsMode, ...rest } = params;
      const apiParams = createApiParams(rest);
      const response = await api.get('/etablissements/new-for-new-companies', {
        params: {
          ...apiParams,
          excludedProspectListIds,
          excludeProspectsMode,
        },
      });
      return standardizeApiResponse(response.data)
    } catch (error) {
      return rejectWithValue(handleApiError(error, 'fetchNewCompanies'));
    }
  }
);

export const fetchNewEstablishmentsInSector = createAsyncThunk(
  'nouveauxAdherents/fetchNewEstablishmentsInSector',
  async (params: FetchParams, { rejectWithValue }) => {
    try {
      const { excludedProspectListIds, excludeProspectsMode, ...rest } = params;
      const apiParams = createApiParams(rest);
      const response = await api.get('/etablissements/new-in-sector', {
        params: {
          ...apiParams,
          excludedProspectListIds,
          excludeProspectsMode,
        },
      });
      return standardizeApiResponse(response.data)
    } catch (error) {
      return rejectWithValue(handleApiError(error, 'fetchNewEstablishmentsInSector'));
    }
  }
);


export const fetchNewEstablishmentsOutOfSector = createAsyncThunk(
  'nouveauxAdherents/fetchNewEstablishmentsOutOfSector',
  async (params: FetchParams, { rejectWithValue }) => {
    try {
      const { excludedProspectListIds, excludeProspectsMode, ...rest } = params;
      const apiParams = createApiParams(rest);
      const response = await api.get('/etablissements/new-out-of-sector', {
        params: {
          ...apiParams,
          excludedProspectListIds,
          excludeProspectsMode,
        },
      });
      return standardizeApiResponse(response.data)
    } catch (error) {
      return rejectWithValue(handleApiError(error, 'fetchNewEstablishmentsOutOfSector'));
    }
  }
);


export const fetchNonClientCompaniesInClientGroups = createAsyncThunk(
  'nouveauxAdherents/fetchNonClientCompaniesInClientGroups',
  async (params: FetchParams, { rejectWithValue }) => {
    try {
      const { excludedProspectListIds, excludeProspectsMode, ...rest } = params;
      const apiParams = createApiParams(rest);
      delete apiParams.dateFrom;
      delete apiParams.dateTo;
      const response = await api.get('/etablissements/non-client-from-client-companies', {
        params: {
          ...apiParams,
          excludedProspectListIds,
          excludeProspectsMode,
        },
      });
      return standardizeApiResponse(response.data)
    } catch (error) {
      return rejectWithValue(handleApiError(error, 'fetchNonClientCompaniesInClientGroups'));
    }
  }
);



export const fetchNafCodes = createAsyncThunk(
  'nouveauxAdherents/fetchNafCodes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/etablissements/naf-level3');
      return response.data as NafCode[];
    } catch (error) {
      const axiosError = error as AxiosError;
      return rejectWithValue(axiosError.message);
    }
  }
);

export const fetchProspectLists = createAsyncThunk(
  'nouveauxAdherents/fetchProspectLists',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<ProspectList[]>('/prospect-lists');
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      return rejectWithValue(axiosError.message);
    }
  }
);

// Define initial state
const initialState: NouveauxAdherentsState = {
  viewData: {
    [ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES]: createEmptyViewState(),
    [ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR]: createEmptyViewState(),
    [ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR]: createEmptyViewState(),
    [ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES]: createEmptyViewState(),
  },
  nafCodes: undefined,
  dateFrom: '',
  dateTo: '',
  departement: '',
  selectedNafCodes: [],
  postalCodes: [],
  excludeMicroEntreprises: false,
  searchTerm: '',
  trancheEffectifsMin: '',
  trancheEffectifsMax: '',
  categorieEntreprise: '',
  currentView: ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES,
  requestSource: ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES,
  isLoadingNafCodes: false,
  nafCodesError: null,
  sortBy: 'dateCreationEtablissement',
  sortOrder: 'DESC',
  prospectLists: [],
  isLoadingProspectLists: false,
  prospectListsError: null,
  excludedProspectListIds: [],
  excludeProspectsMode: 'siret',
};

// Create slice
const nouveauxAdherentsSlice = createSlice({
  name: 'nouveauxAdherents',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      // Update the page for the current view only
      state.viewData[state.currentView].page = action.payload;
    },
    setCurrentView: (state, action: PayloadAction<ProspectionViewType>) => {
      state.currentView = action.payload;
      state.requestSource = action.payload;
      
      // Reset the current page to 1 when switching views to avoid pagination issues
      state.viewData[action.payload].page = 1;
    },
    setDateFrom: (state, action: PayloadAction<string>) => {
      state.dateFrom = action.payload;
    },
    setDateTo: (state, action: PayloadAction<string>) => {
      state.dateTo = action.payload;
    },
    setDepartement: (state, action: PayloadAction<string>) => {
      state.departement = action.payload;
    },
    setSelectedNafCodes: (state, action: PayloadAction<string[]>) => {
      state.selectedNafCodes = action.payload;
    },
    setExcludeMicroEntreprises: (state, action: PayloadAction<boolean>) => {
      state.excludeMicroEntreprises = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setTrancheEffectifsMin: (state, action: PayloadAction<string>) => {
      state.trancheEffectifsMin = action.payload;
    },
    setTrancheEffectifsMax: (state, action: PayloadAction<string>) => {
      state.trancheEffectifsMax = action.payload;
    },
    setCategorieEntreprise: (state, action: PayloadAction<string>) => {
      state.categorieEntreprise = action.payload;
    },
    resetError: (state) => {
      state.viewData[state.currentView].error = null;
    },
    setPostalCodes: (state, action: PayloadAction<string[]>) => {
      state.postalCodes = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<string>) => {
      state.sortOrder = action.payload;
    },
    setSorting: (state, action: PayloadAction<{ sortBy: string; sortOrder: string }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    setExcludedProspectListIds: (state, action: PayloadAction<number[]>) => {
      state.excludedProspectListIds = action.payload;
    },
    setExcludeProspectsMode: (state, action: PayloadAction<'siret' | 'siren'>) => {
      state.excludeProspectsMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchNewCompanies
    builder
      .addCase(fetchNewCompanies.pending, (state) => {
        const targetView = ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES;
        state.viewData[targetView].isLoading = true;
        state.viewData[targetView].error = null;
        state.requestSource = targetView;
      })
      .addCase(fetchNewCompanies.fulfilled, (state, action: { payload: FetchPayload }) => {
        // Get the view and data from the payload
        const view = ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES;
        const { establishments, pagination } = action.payload as FetchPayload;
        
        // Update the view's data
        if (view && Object.keys(state.viewData).includes(view)) {
          state.viewData[view].isLoading = false;
          state.viewData[view].establishments = establishments;
          state.viewData[view].total = pagination.total;
          state.viewData[view].page = pagination.page;
          state.viewData[view].totalPages = pagination.pages;
        }
      })
      .addCase(fetchNewCompanies.rejected, (state, action) => {
        const targetView = ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES;
        state.viewData[targetView].isLoading = false;
        state.viewData[targetView].error = action.payload as string;
      });
    // Handle fetchNewEstablishmentsInSector
    builder
      .addCase(fetchNewEstablishmentsInSector.pending, (state) => {
        const targetView = ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR;
        state.viewData[targetView].isLoading = true;
        state.viewData[targetView].error = null;
        state.requestSource = targetView;
      })
      .addCase(fetchNewEstablishmentsInSector.fulfilled, (state, action: { payload: FetchPayload }) => {
        // Get the view and data from the payload
        const view = ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR;
        const { establishments, pagination } = action.payload as FetchPayload;
        
        // Update the view's data
        if (view && Object.keys(state.viewData).includes(view)) {
          state.viewData[view].isLoading = false;
          state.viewData[view].establishments = establishments;
          state.viewData[view].total = pagination.total;
          state.viewData[view].page = pagination.page;
          state.viewData[view].totalPages = pagination.pages;
        }
      })
      .addCase(fetchNewEstablishmentsInSector.rejected, (state, action) => {
        const targetView = ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR;
        state.viewData[targetView].isLoading = false;
        state.viewData[targetView].error = action.payload as string;
      });

    // Handle fetchNewEstablishmentsOutOfSector
    builder
      .addCase(fetchNewEstablishmentsOutOfSector.pending, (state) => {
        const targetView = ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR;
        state.viewData[targetView].isLoading = true;
        state.viewData[targetView].error = null;
        state.requestSource = targetView;
      })
      .addCase(fetchNewEstablishmentsOutOfSector.fulfilled, (state, action: { payload: FetchPayload }) => {
        // Get the view and data from the payload
        const view = ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR;
        const { establishments, pagination } = action.payload as FetchPayload;
        
        // Update the view's data
        if (view && Object.keys(state.viewData).includes(view)) {
          state.viewData[view].isLoading = false;
          state.viewData[view].establishments = establishments;
          state.viewData[view].total = pagination.total;
          state.viewData[view].page = pagination.page;
          state.viewData[view].totalPages = pagination.pages;
        }
      })
      .addCase(fetchNewEstablishmentsOutOfSector.rejected, (state, action) => {
        const targetView = ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR;
        state.viewData[targetView].isLoading = false;
        state.viewData[targetView].error = action.payload as string;
      });

    // Handle fetchNonClientCompaniesInClientGroups
    builder
      .addCase(fetchNonClientCompaniesInClientGroups.pending, (state) => {
        const targetView = ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES;
        state.viewData[targetView].isLoading = true;
        state.viewData[targetView].error = null;
        state.requestSource = targetView;
      })
      .addCase(fetchNonClientCompaniesInClientGroups.fulfilled, (state, action: { payload: FetchPayload }) => {
        // Get the view and data from the payload
        const view = ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES;
        const { establishments, pagination } = action.payload as FetchPayload;
        
        // Update the view's data
        if (view && Object.keys(state.viewData).includes(view)) {
          state.viewData[view].isLoading = false;
          state.viewData[view].establishments = establishments;
          state.viewData[view].total = pagination.total;
          state.viewData[view].page = pagination.page;
          state.viewData[view].totalPages = pagination.pages;
        }
      })
      .addCase(fetchNonClientCompaniesInClientGroups.rejected, (state, action) => {
        const targetView = ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES;
        state.viewData[targetView].isLoading = false;
        state.viewData[targetView].error = action.payload as string;
      });

    // Handle fetchNafCodes
    builder
      .addCase(fetchNafCodes.pending, (state) => {
        // NAF codes have their own dedicated loading state
        state.isLoadingNafCodes = true;
        state.nafCodesError = null;
      })
      .addCase(fetchNafCodes.fulfilled, (state, action) => {
        state.isLoadingNafCodes = false;
        state.nafCodes = action.payload;
      })
      .addCase(fetchNafCodes.rejected, (state, action) => {
        state.isLoadingNafCodes = false;
        state.nafCodesError = action.payload as string;
      });

    builder
      .addCase(fetchProspectLists.pending, (state) => {
        state.isLoadingProspectLists = true;
        state.prospectListsError = null;
      })
      .addCase(fetchProspectLists.fulfilled, (state, action) => {
        state.isLoadingProspectLists = false;
        state.prospectLists = action.payload;
      })
      .addCase(fetchProspectLists.rejected, (state, action) => {
        state.isLoadingProspectLists = false;
        state.prospectListsError = action.payload as string;
      });
  },
});

// Export actions
export const {
  setCurrentPage,
  setCurrentView,
  setDateFrom,
  setDateTo,
  setDepartement,
  setSelectedNafCodes,
  setExcludeMicroEntreprises,
  setSearchTerm,
  setTrancheEffectifsMin,
  setTrancheEffectifsMax,
  setCategorieEntreprise,
  resetError,
  setPostalCodes,
  setSortBy,
  setSortOrder,
  setSorting,
  setExcludedProspectListIds,
  setExcludeProspectsMode,
} = nouveauxAdherentsSlice.actions;

// Export selectors
export const selectEstablishments = (state: RootState) => 
  state.nouveauxAdherents.viewData[state.nouveauxAdherents.currentView].establishments;
export const selectNafCodes = (state: RootState) => state.nouveauxAdherents.nafCodes;
export const selectIsLoadingNafCodes = (state: RootState) => state.nouveauxAdherents.isLoadingNafCodes;
export const selectNafCodesError = (state: RootState) => state.nouveauxAdherents.nafCodesError;
export const selectIsLoading = (state: RootState) => 
  state.nouveauxAdherents.viewData[state.nouveauxAdherents.currentView].isLoading;
export const selectTotal = (state: RootState) => 
  state.nouveauxAdherents.viewData[state.nouveauxAdherents.currentView].total;
export const selectCurrentPage = (state: RootState) => 
  state.nouveauxAdherents.viewData[state.nouveauxAdherents.currentView].page;
export const selectTotalPages = (state: RootState) => 
  state.nouveauxAdherents.viewData[state.nouveauxAdherents.currentView].totalPages;
export const selectDateFrom = (state: RootState) => state.nouveauxAdherents.dateFrom;
export const selectDateTo = (state: RootState) => state.nouveauxAdherents.dateTo;
export const selectDepartement = (state: RootState) => state.nouveauxAdherents.departement;
export const selectSelectedNafCodes = (state: RootState) => state.nouveauxAdherents.selectedNafCodes;
export const selectExcludeMicroEntreprises = (state: RootState) => state.nouveauxAdherents.excludeMicroEntreprises;
export const selectSearchTerm = (state: RootState) => state.nouveauxAdherents.searchTerm;
export const selectTrancheEffectifsMin = (state: RootState) => state.nouveauxAdherents.trancheEffectifsMin;
export const selectTrancheEffectifsMax = (state: RootState) => state.nouveauxAdherents.trancheEffectifsMax;
export const selectCategorieEntreprise = (state: RootState) => state.nouveauxAdherents.categorieEntreprise;
export const selectCurrentView = (state: RootState) => state.nouveauxAdherents.currentView;
export const selectError = (state: RootState) => 
  state.nouveauxAdherents.viewData[state.nouveauxAdherents.currentView].error;

export const selectPostalCodes = (state: RootState) => state.nouveauxAdherents.postalCodes;
export const selectSortBy = (state: RootState) => state.nouveauxAdherents.sortBy;
export const selectSortOrder = (state: RootState) => state.nouveauxAdherents.sortOrder;
export const selectProspectLists = (state: RootState) => state.nouveauxAdherents.prospectLists;
export const selectIsLoadingProspectLists = (state: RootState) =>
  state.nouveauxAdherents.isLoadingProspectLists;
export const selectExcludedProspectListIds = (state: RootState) =>
  state.nouveauxAdherents.excludedProspectListIds;
export const selectExcludeProspectsMode = (state: RootState) =>
  state.nouveauxAdherents.excludeProspectsMode;

export default nouveauxAdherentsSlice.reducer; 